// Client for /api/pa/chat SSE streaming (PA-1b).

import { getCapabilityOverrides } from "@/lib/capabilities/capabilityStorage";
import { PA_DEV_IMPERSONATION_HEADER } from "@/lib/personalAssistant/paDevImpersonationHeader";
import type { AssistMode } from "@/lib/personalAssistant/paAssistModeStorage";
import { parsePaProposedTools } from "@/lib/personalAssistant/paToolCatalog";
import type { PaChatStreamEvent, PaInputModality, PaThreadMessage } from "@/lib/personalAssistant/paThreadTypes";

export type PaChatRequest = {
  message: string;
  token: string;
  organizationId: string | null;
  assistMode: AssistMode;
  inputModality?: PaInputModality;
  currentMode?: string | null;
  impersonationActive?: boolean;
};

function parseSseBlock(block: string): PaChatStreamEvent | null {
  let eventName = "message";
  let dataLine = "";
  for (const line of block.split("\n")) {
    if (line.startsWith("event: ")) eventName = line.slice(7).trim();
    if (line.startsWith("data: ")) dataLine = line.slice(6);
  }
  if (!dataLine) return null;
  try {
    const data = JSON.parse(dataLine) as Record<string, unknown>;
    if (eventName === "delta" && typeof data.text === "string") {
      return { type: "delta", text: data.text };
    }
    if (eventName === "tools" && Array.isArray(data.proposedTools)) {
      return {
        type: "tools",
        proposedTools: parsePaProposedTools(data.proposedTools),
        assistantMessageId: String(data.assistantMessageId ?? ""),
      };
    }
    if (eventName === "done") {
      return {
        type: "done",
        userMessageId: String(data.userMessageId ?? ""),
        assistantMessageId: String(data.assistantMessageId ?? ""),
        proposedTools: Array.isArray(data.proposedTools)
          ? parsePaProposedTools(data.proposedTools)
          : undefined,
        usage:
          data.usage && typeof data.usage === "object"
            ? {
                promptTokensEst:
                  typeof (data.usage as { promptTokensEst?: unknown }).promptTokensEst === "number"
                    ? (data.usage as { promptTokensEst: number }).promptTokensEst
                    : undefined,
                completionTokensEst:
                  typeof (data.usage as { completionTokensEst?: unknown }).completionTokensEst ===
                  "number"
                    ? (data.usage as { completionTokensEst: number }).completionTokensEst
                    : undefined,
              }
            : undefined,
      };
    }
    if (eventName === "error") {
      return { type: "error", message: String(data.message ?? "Assistant error") };
    }
  } catch {
    return null;
  }
  return null;
}

export async function* streamPaChat(
  req: PaChatRequest,
): AsyncGenerator<PaChatStreamEvent> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${req.token}`,
    "Content-Type": "application/json",
  };
  if (req.organizationId) {
    headers["x-workspace-organization-id"] = req.organizationId;
  }
  if (req.impersonationActive) {
    headers[PA_DEV_IMPERSONATION_HEADER] = "true";
  }

  const resp = await fetch("/api/pa/chat", {
    method: "POST",
    headers,
    body: JSON.stringify({
      message: req.message,
      assistMode: req.assistMode,
      capabilityOverrides: getCapabilityOverrides(),
      inputModality: req.inputModality ?? "text",
      currentMode: req.currentMode ?? null,
    }),
  });

  if (!resp.ok) {
    const payload = (await resp.json().catch(() => null)) as { error?: string } | null;
    yield { type: "error", message: payload?.error ?? `Chat failed (${resp.status})` };
    return;
  }

  if (!resp.body) {
    yield { type: "error", message: "Empty assistant response" };
    return;
  }

  const decoder = new TextDecoder();
  const reader = resp.body.getReader();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";
      for (const part of parts) {
        const evt = parseSseBlock(part.trim());
        if (evt) yield evt;
      }
    }
    if (buffer.trim()) {
      const evt = parseSseBlock(buffer.trim());
      if (evt) yield evt;
    }
  } finally {
    reader.releaseLock();
  }
}

export async function fetchPaThreadCurrent(args: {
  token: string;
  organizationId: string | null;
  assistMode: AssistMode;
  impersonationActive?: boolean;
}): Promise<PaThreadMessage[]> {
  const params = new URLSearchParams({
    assistMode: args.assistMode,
    capabilityOverrides: JSON.stringify(getCapabilityOverrides()),
  });
  const headers: Record<string, string> = {
    Authorization: `Bearer ${args.token}`,
  };
  if (args.organizationId) {
    headers["x-workspace-organization-id"] = args.organizationId;
  }
  if (args.impersonationActive) {
    headers[PA_DEV_IMPERSONATION_HEADER] = "true";
  }

  const resp = await fetch(`/api/pa/threads/current?${params.toString()}`, { headers });
  if (!resp.ok) {
    const payload = (await resp.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? `Failed to load thread (${resp.status})`);
  }
  const json = (await resp.json()) as { messages?: PaThreadMessage[] };
  return Array.isArray(json.messages) ? json.messages : [];
}
